import { inject, Injectable } from '@angular/core';
import { LoyaltyProgramFacade } from '@features/loyalty-program/facades/loyalty-program.facade';
import { Store } from '@ngrx/store';
import { getAuthIsLoggedIn } from '@rd/core/auth/store/selectors/auth.selectors';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { NavLink } from '@rd/types';
import { getElementBy } from '@rd/utils/getElementBy';
import { isNotNull } from '@rd/utils/isNotNull';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { RewardsProgramTransferConnectIdentifier } from '../../rewards-program/types';
import {
  BNZ_BANKWIDE_UPDATES_ENABLED,
  BNZ_DEPRECATE_FLYBUYS_SCHEME_ENABLED,
  BNZ_USER_HAS_ENROLLMENTS,
} from '../tokens/features.tokens';

@Injectable({
  providedIn: 'root',
})
export class BnzNavLinksService {
  constructor(
    private store: Store,
    private urlUtilsService: UrlUtilsService,
    private loyaltyProgramFacade: LoyaltyProgramFacade,
  ) {}

  isBnzBankwideUpdatesEnabled = inject(BNZ_BANKWIDE_UPDATES_ENABLED);
  isDeprecateFlybuysSchemeEnabled: boolean = inject(
    BNZ_DEPRECATE_FLYBUYS_SCHEME_ENABLED,
  );
  userHasEnrollments$: Observable<boolean> = inject(BNZ_USER_HAS_ENROLLMENTS);

  isLoggedIn$: Observable<boolean> = this.store
    .select(getAuthIsLoggedIn)
    .pipe(filter(isNotNull));

  flyBuysLoyaltyProgramId$: Observable<string> =
    this.loyaltyProgramFacade.loyaltyPrograms$.pipe(
      getElementBy(
        (lp) =>
          lp.transferConnectIdentifier ===
          RewardsProgramTransferConnectIdentifier.Flybuys,
      ),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      map((lp) => lp?.id!),
    );

  home: NavLink = {
    translationKey: 'wl.bnz.header.home',
    route: '/',
  };

  offers: NavLink = {
    translationKey: 'wl.bnz.header.offers',
    route: '/offers',
  };

  redemptions: NavLink[] = [
    {
      translationKey: 'wl.bnz.header.travel',
      iconUrl: 'icon-booktravel',
      route: this.urlUtilsService.travelEdgeUrl('/flights').toString(),
    },
    {
      translationKey: 'wl.bnz.header.gift_cards',
      iconUrl: 'icon-gift-cards',
      route: '/gift_card/listing',
    },
    {
      translationKey: 'wl.bnz.header.cashback',
      iconUrl: 'icon-redeem-cash',
      route: '/redeem_cash',
    },
    {
      translationKey: 'wl.bnz.header.points_transfer',
      iconUrl: 'icon-points-transfer',
      route: '/transfer/programs',
    },
  ];

  // special case of redemptions
  flybuysRedemptionLink$: Observable<NavLink | null> =
    this.flyBuysLoyaltyProgramId$.pipe(
      map((lpId) =>
        !this.isDeprecateFlybuysSchemeEnabled
          ? {
              translationKey: 'wl.bnz.header.fly_buy',
              iconUrl: 'icon-flyby',
              route: `/transfer/checkout/flybuys/${lpId}`,
            }
          : null,
      ),
    );

  myAccount$: Observable<NavLink[]> = combineLatest([
    this.isLoggedIn$,
    this.userHasEnrollments$,
  ]).pipe(
    map(([isLoggedIn, _]: [boolean, boolean]) => {
      const poolPointsEnabled = isLoggedIn;

      return [
        {
          translationKey: 'wl.bnz.header.points_activity',
          route: '/points_activity',
        },
        {
          ...(poolPointsEnabled && {
            translationKey: 'wl.bnz.header.pool_points',
            route: '/share_points',
          }),
        } as NavLink,
        {
          translationKey: 'wl.bnz.header.manage_booking',
          route: this.urlUtilsService
            .travelEdgeUrl('/account/booking')
            .toString(),
        },
        {
          ...(!this.isDeprecateFlybuysSchemeEnabled && {
            translationKey: 'wl.bnz.header.rewards_scheme',
            route: '/rewards_programs',
          }),
        } as NavLink,
        {
          translationKey: 'wl.bnz.header.faq',
          route: '/faq',
        },
      ].filter((navLink) => Object.keys(navLink).length);
    }),
  );

  myAccountMobile$: Observable<NavLink[]> = combineLatest([
    this.isLoggedIn$,
    this.userHasEnrollments$,
  ]).pipe(
    map(([isLoggedIn, _]: [boolean, boolean]) => {
      const poolPointsEnabled = isLoggedIn;

      return [
        {
          translationKey: 'wl.bnz.header.points_activity',
          iconUrl: 'icon-points-activity',
          route: '/points_activity',
        },
        {
          ...(poolPointsEnabled && {
            translationKey: 'wl.bnz.header.pool_points',
            iconUrl: 'icon-pool-points',
            route: '/share_points',
          }),
        } as NavLink,
        {
          translationKey: 'wl.bnz.header.manage_booking',
          iconUrl: 'icon-manage-booking',
          route: this.urlUtilsService
            .travelEdgeUrl('/account/booking')
            .toString(),
        },
        {
          ...(!this.isDeprecateFlybuysSchemeEnabled && {
            translationKey: 'wl.bnz.header.rewards_scheme',
            iconUrl: 'icon-rewards-scheme',
            route: '/rewards_programs',
          }),
        } as NavLink,
      ].filter((navLink) => Object.keys(navLink).length);
    }),
  );

  staticPages: NavLink[] = [
    {
      translationKey: 'wl.bnz.header.faq',
      iconUrl: 'icon-faq',
      route: '/faq',
    },
    {
      translationKey: 'wl.bnz.header.bnz_terms_and_conditions_update',
      iconUrl: 'icon-terms-and-conditions',
      route: this.isBnzBankwideUpdatesEnabled
        ? 'http://bnz.co.nz/rewardsterms'
        : 'https://www.bnz.co.nz/about-us/governance/terms-and-conditions#cards',
    },
    {
      translationKey: 'wl.bnz.header.terms_and_conditions_update',
      iconUrl: 'icon-terms-and-conditions',
      route: '/terms_and_conditions',
    },
    {
      translationKey: 'wl.bnz.header.privacy_policy',
      iconUrl: 'icon-privacy-policy',
      route: '/privacy_policy',
    },
  ];
}
